/* globals window document jQuery appSettings */
import dateScroller from '../inventoryOverview/dateScroller';
import dateScrollerEvents from '../inventoryOverview/dateScroller/events';
import citySelectorEvents from '../../components/citySelector/events';
import factory from '../../components/factory';
import filters from '../filters';
import filterEvents from '../filters/events';
import classesList from './list';
import toggleClass from '../../utils/toggleClass';
import setDateScrollerDate from '../inventoryOverview/dateScroller/utils/dateScrollerActions';
import listEvents from './list/events';
import matchBreakpoint from '../../utils/matchBreakpoint';
import http from '../../utils/http';
import eventTracker from '../../components/eventTracker';
import pageView from '../../components/eventTracker/dataLayer/pageView';

((window, document, $, appSettings) => {
  const { isLoggedIn } = appSettings;
  const classesListElement = document.querySelector('[data-js-classesList]');
  const dateElement = document.querySelector('[data-js-dateScroller]');
  const toggleFilterContainer = document.querySelector('[data-js-toggleFilterContainer]');
  const filtersElement = document.querySelector('[data-js-filters]');
  const showFilters = document.querySelector('[data-js-showFilters]');
  const loader = document.querySelector('[data-js-loader]');

  const $document = $(document);
  const $dateElement = $(dateElement);
  const $classesList = $(classesListElement);
  const $filters = $(filtersElement);
  const $showFilters = $(showFilters);
  const $scrollContainer = !matchBreakpoint('xsmall') ? $(window) : $(document);

  const filterSet = ['city', 'category', 'is_digital', 'partner_ids', 'userLocation', 'favoriteLocations', 'workout_time', 'get_favorite_schedules', 'query'];
  let currentDate;
  let filtersParams = {};
  let page = 1;
  let hasFilterTimeout;


  /**
   * init function
   */
  function init() {
    $document.trigger(citySelectorEvents.CITY_SELECTED, { cityCode: window.city });
    factory(classesList, '[data-js-classesList]');
    factory(dateScroller, '[data-js-dateScroller]');
    filters(filtersElement, { $scrollContainer, filterSet, isLoggedIn });

    pushPageViewToDatalayer();

    bindEvents();
  }

  /**
   * bind events with handlers
   */
  function bindEvents() {
    $filters
      .on(filterEvents.CHANGE, onFilterChange)
      .on(filterEvents.HIDE, onFilterHide)
      .on(filterEvents.NUM_SELECTED_FILTERS_CHANGED, onNumSelectedFiltersChanged);

    if (!matchBreakpoint('xsmall')) {
      $showFilters.on('click', onShowFiltersClick);
    }

    Promise.all([getLocations(), getCategories()])
      .then(onDataLoadDone);

    $document.on(dateScrollerEvents.DATE_SELECTED, onDateSelected);
    setDateScrollerDate($dateElement);

    $document.scroll(handleDocumentScrolling);
  }

  /**
   * triggered on document scrolling
   */
  function handleDocumentScrolling() {
    handleToggleFilterBarPosition();
    handleListLoadingScrolling();
  }

  /**
   * push pageView event to datalayer when the page load
   */
  function pushPageViewToDatalayer() {
    eventTracker.dataLayerPush(pageView({
      pageTitle: 'classes search',
      pageCategory: 'search',
    }));
  }

  /**
   * Updated filters event handler
   * @param {Object} locations
   * @param {Object} categories
   */
  function onUpdatedFilters(locations, categories) {
    const mapOptions = ({ id, name }) => ({ id, name });

    $filters.trigger(filterEvents.SET_DATA, {
      locations: locations.map(mapOptions),
      categories: categories.map(mapOptions),
    });
  }

  /**
   * Updated categories event handler
   * @param {Object} categories
   * @param {object} locations
   */
  function onUpdatedList(categories, locations) {
    $classesList.trigger(listEvents.SET_DATA, { categories, locations });
  }

  /**
   * Updated locations event handler
   * @param {Object} locations
   */
  function onUpdatedLocations(locations) {
    const mapOptions = ({ id, name }) => ({ id, name });

    loader.classList.add('_is_hidden');

    $filters.trigger(filterEvents.SET_DATA, {
      locations: locations.map(mapOptions),
    });
  }

  /**
   * Distributes all loaded data to the components
   * @param {Object} locations
   * @param {Object} categories
   */
  function onDataLoadDone([locations, categories]) {
    onUpdatedFilters(locations.data, categories.data);
    onUpdatedList(categories.data, locations.data);
    onUpdatedLocations(locations.data);
  }

  /**
   * Returns a promise while fetching the locations
   * @param {Object} requestParams
   * @return {Promise}
   */
  function getLocations(requestParams) {
    loader.classList.remove('_is_hidden');

    return new Promise((resolve) => {
      http.get(`${appSettings.apiUrlV2}/partners/city/${window.city}`, requestParams)
        .then(resolve);
    });
  }

  /**
   * Returns a promise while fetching the catagories
   * @return {Promise}
   */
  function getCategories() {
    return new Promise((resolve) => {
      http.get(`${appSettings.apiUrlV2}/workouts/categories`)
        .then(resolve);
    });
  }

  /**
   * Show filters click event handler
   */
  function onShowFiltersClick() {
    document.body.classList.add('_has_fullscreen_overlay');
    $filters.trigger(filterEvents.SHOW);
  }

  /**
   * Num selected filters changed event handler
   * @param {Event} event
   * @param {Object} eventData
   */
  function onNumSelectedFiltersChanged(event, { numSelectedFilters }) {
    if (numSelectedFilters) {
      $showFilters
        .find('[data-js-numSelectedFilters]')
        .addClass('_has_count')
        .attr('data-js-numSelectedFilters', numSelectedFilters);
    } else {
      $showFilters
        .find('[data-js-numSelectedFilters]')
        .removeClass('_has_count');
    }
  }

  /**
   * Filter hide event handler
   */
  function onFilterHide() {
    document.body.classList.remove('_has_fullscreen_overlay');
  }

  /**
   * Filter change event handler
   * @param {DOMEvent} event
   * @param {Object} eventData
   */
  function onFilterChange(event, eventData) {
    if (hasFilterTimeout) {
      clearTimeout(hasFilterTimeout);
    }

    filtersParams = eventData;

    hasFilterTimeout = setTimeout(() => {
      page = 1;
      $classesList.trigger(listEvents.SET_DATA, { page, filtersParams, currentDate });
    }, 500);
  }

  /**
   * Date selected event handler
   * @param {DOMEvent} event
   * @param {Object} eventData
   */
  function onDateSelected(event, { date }) {
    currentDate = date;
    page = 1;
    $classesList.trigger(listEvents.SET_DATA, { page, filtersParams, currentDate });
  }

  /**
   * load the new locations when we reached the middle of the list
   */
  function handleListLoadingScrolling() {
    $classesList.trigger(listEvents.SCROLL, { currentDate, filtersParams });
  }

  /**
   * handle toggling the "fixed" class for the filter bar while scrolling
   */
  function handleToggleFilterBarPosition() {
    if ($(classesListElement).offset().top - 100 <= $document.scrollTop()) {
      if (toggleFilterContainer && !toggleFilterContainer.classList.contains('fixed')) {
        toggleClass(toggleFilterContainer, 'fixed', true);
      }
    } else if (toggleFilterContainer && toggleFilterContainer.classList.contains('fixed')) {
      toggleClass(toggleFilterContainer, 'fixed', false);
    }
  }

  $document.ready(init);
})(window, document, jQuery, appSettings);



// WEBPACK FOOTER //
// ./resources/assets/js/specifics/classes/classes.js